import { useState } from 'react';
import { Pause, Play, Phone, Mail, MessageSquare, CalendarCheck, Clock, UserPlus, X, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge } from './StatusBadge';
import { useConversa, useHistorico, useLeadsDisponiveis, useVincularLead, useDesvincularLead, useCriarLead } from '@/hooks/useIAWhatsApp';
import type { Conversa } from '@/types';

interface ConversaDetalhesProps {
  conversa: Conversa;
  onToggleIA: () => void;
}

function formatPhone(numero: string | null): string {
  if (!numero) return 'Não identificado';
  const cleaned = numero.replace(/\D/g, '');
  if (cleaned.length === 11) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  if (cleaned.length === 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  return numero;
}

export function ConversaDetalhes({ conversa, onToggleIA }: ConversaDetalhesProps) {
  const [showVincular, setShowVincular] = useState(false);
  const [searchLead, setSearchLead] = useState('');

  const { data: detalhe } = useConversa(conversa.channel_id);
  const { data: historico } = useHistorico(conversa.channel_id, 10);
  const { data: leadsDisponiveis, isLoading: loadingLeads } = useLeadsDisponiveis(searchLead);
  const vincular = useVincularLead();
  const desvincular = useDesvincularLead();
  const criarLead = useCriarLead();

  const displayName = conversa.lead_nome || formatPhone(conversa.numero_real) || conversa.channel_id;
  const isDisabled = conversa.is_ai_disabled;

  const handleVincular = (leadId: number) => {
    vincular.mutate(
      { channelId: conversa.channel_id, leadId },
      { onSuccess: () => setShowVincular(false) }
    );
  };

  const handleDesvincular = () => {
    if (!conversa.lead_id) return;
    desvincular.mutate({ channelId: conversa.channel_id, leadId: conversa.lead_id });
  };

  const handleCriarLead = () => {
    const rawPhone = (conversa.numero_real || conversa.channel_id || '').replace(/\D/g, '');
    const fallbackName = rawPhone ? `Lead ${rawPhone}` : 'Lead WhatsApp';
    const nome = (conversa.push_name || conversa.lead_nome || fallbackName).trim();

    criarLead.mutate(
      {
        nome,
        telefone: rawPhone || conversa.numero_real || conversa.channel_id,
        fonte: 'whatsapp'
      },
      {
        onSuccess: (result) => {
          const leadId = result?.data?.lead?.id;
          if (!leadId) return;
          vincular.mutate(
            { channelId: conversa.channel_id, leadId },
            {
              onSuccess: () => {
                setShowVincular(false);
                setSearchLead('');
              }
            }
          );
        },
      }
    );
  };

  return (
    <div className="space-y-4">
      {/* Cabeçalho do Lead */}
      <Card className="border-ds-border bg-ds-surface">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              isDisabled ? 'bg-ds-danger/10' : 'bg-ds-success/10'
            }`}>
              <span className={`text-lg font-semibold ${isDisabled ? 'text-ds-danger' : 'text-ds-success'}`}>
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-ds-text-primary truncate">{displayName}</h3>
              <div className="flex items-center gap-3 mt-1 text-sm text-ds-text-secondary">
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {formatPhone(conversa.numero_real || conversa.channel_id)}
                </span>
                {detalhe?.lead_email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {detalhe.lead_email}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <StatusBadge
              is_ai_disabled={conversa.is_ai_disabled}
              msg_count_24h={conversa.msg_count_24h}
              teve_reativacao_recente={conversa.teve_reativacao_recente}
            />
          </div>
        </CardContent>
      </Card>

      {/* Controle de IA */}
      <Card className={`border-ds-border ${isDisabled ? 'bg-ds-danger/[0.02]' : 'bg-ds-surface'}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-ds-text-primary">Controle da IA</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Button
            variant={isDisabled ? 'primary' : 'danger'}
            size="lg"
            className="w-full"
            onClick={onToggleIA}
          >
            {isDisabled ? (
              <>
                <Play className="h-5 w-5 mr-2" />
                Ligar IA nesta conversa
              </>
            ) : (
              <>
                <Pause className="h-5 w-5 mr-2" />
                Desligar IA nesta conversa
              </>
            )}
          </Button>
          <p className="text-xs text-ds-text-secondary mt-2 text-center">
            {isDisabled
              ? 'A IA está pausada. Você está no controle do atendimento.'
              : 'A IA está respondendo automaticamente. Desligue para assumir o atendimento.'}
          </p>
        </CardContent>
      </Card>

      {/* Lead Vinculado */}
      <Card className="border-ds-border bg-ds-surface">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-ds-text-primary">Lead Vinculado</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {conversa.lead_id ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-ds-text-primary">{conversa.lead_nome}</p>
                {detalhe?.lead_status && (
                  <Badge variant="default" className="mt-1">
                    {detalhe.lead_status}
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={handleDesvincular}>
                <X className="h-4 w-4 mr-1" />
                Remover
              </Button>
            </div>
          ) : (
            <div>
              {!showVincular ? (
                <Button variant="secondary" size="sm" onClick={() => setShowVincular(true)}>
                  <UserPlus className="h-4 w-4 mr-1.5" />
                  Vincular a um Lead
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
                    <input
                      type="text"
                      placeholder="Buscar lead..."
                      value={searchLead}
                      onChange={(e) => setSearchLead(e.target.value)}
                      className="w-full bg-ds-surface2 border border-ds-border rounded-lg pl-9 pr-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-success/50"
                    />
                  </div>
                  {loadingLeads && (
                    <div className="flex items-center gap-2 text-xs text-ds-text-secondary py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Buscando...
                    </div>
                  )}
                  {leadsDisponiveis && leadsDisponiveis.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1 border border-ds-border rounded-lg">
                      {leadsDisponiveis.map((lead) => (
                        <button
                          key={lead.id}
                          onClick={() => handleVincular(lead.id)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-ds-surface2 transition-colors"
                        >
                          <p className="text-ds-text-primary font-medium">{lead.nome}</p>
                          <p className="text-ds-text-secondary text-xs">{lead.email || lead.telefone || 'Sem contato'}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchLead.length >= 2 && leadsDisponiveis?.length === 0 && !loadingLeads && (
                    <p className="text-xs text-ds-text-secondary">Nenhum lead encontrado</p>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowVincular(false);
                        setSearchLead('');
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCriarLead}
                      disabled={criarLead.isPending || vincular.isPending}
                    >
                      {criarLead.isPending || vincular.isPending ? 'Criando...' : 'Criar Lead'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resumo do Histórico */}
      <Card className="border-ds-border bg-ds-surface">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-ds-text-primary flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Últimas mensagens
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {historico && historico.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {historico.map((msg) => (
                <div key={msg.id} className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${
                      msg.type === 'human' ? 'text-ds-info' :
                      msg.type === 'ai' ? 'text-ds-success' :
                      'text-ds-text-secondary'
                    }`}>
                      {msg.type === 'human' ? 'Humano' : msg.type === 'ai' ? 'IA' : msg.type || 'Sistema'}
                    </span>
                    <span className="text-xs text-ds-text-secondary">
                      {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-ds-text-secondary mt-0.5 line-clamp-2">{msg.conteudo || '(sem conteúdo)'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-ds-text-secondary">Sem mensagens recentes</p>
          )}
        </CardContent>
      </Card>

      {/* Informações adicionais */}
      <Card className="border-ds-border bg-ds-surface">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-ds-text-secondary">Total de mensagens</span>
            <span className="text-ds-text-primary font-medium">{detalhe?.total_mensagens ?? '-'}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-ds-text-secondary">Mensagens 24h</span>
            <span className="text-ds-text-primary font-medium">{conversa.msg_count_24h}</span>
          </div>
          {conversa.teve_reativacao_recente && (
            <div className="flex items-center gap-2 text-sm text-ds-info">
              <CalendarCheck className="h-4 w-4" />
              <span>Reativação recente</span>
            </div>
          )}
          {isDisabled && conversa.disabled_since && (
            <div className="flex items-center gap-2 text-sm text-ds-danger">
              <Clock className="h-4 w-4" />
              <span>Desde {new Date(conversa.disabled_since).toLocaleString('pt-BR')}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
